import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { Checklist, Confirm, Icons, Modal, PinAction } from '@components/common';
import CustomPolygonForm from '@components/ModalComponents/CustomPolygonForm';
import { TSetCustomPolygonData } from '@components/ModalComponents/CustomPolygonForm/CustomPolygonForm';

import { useMutateDeletePolygons } from '@hooks/polygons/useMutateDeletePolygon';
import { useQueryPolygons } from '@hooks/polygons/useQueryPolygons';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	hideCustomPolygonsOnMap,
	resetActiveCustomPolygons,
	resetRightSideControls,
	resetShowingCustomPolygonIds,
	setPinnedFilter,
	setSelectedCustomPolygonForView,
	setShowingCustomPolygonIds,
} from '@slices/map';

import { IChecklistEntity } from '@interfaces/assets/asset';
import { ICustomPolygonDTO } from '@interfaces/map/dto/polygonDTO';
import { TCustomPolygon } from '@interfaces/map/polygon';
import { ILastChangedItem } from '@components/common/Checklist/Checklist.types';

import { EControlsType, TEControlsType } from '@enums/common';

import { SEARCH_DELAY_ON_MAP } from '@constants/common';
import { MODAL_WIDTH_480 } from '@constants/modals';

import styles from './CustomPolygonList.module.scss';
import controlStyles from '@components/Map/MapControls/MapControls.module.scss';

const CustomPolygonList = () => {
	const dispatch = useAppDispatch();
	const { showingCustomPolygonIds } = useAppSelector(map);

	const { polygons: customPolygons } = useQueryPolygons();
	const { deleteCustomPolygons } = useMutateDeletePolygons();

	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
	const [isEditPolygonOpen, setIsEditPolygonOpen] = useState(false);
	const [isDirtyPolygonForm, setIsDirtyPolygonForm] = useState(false);
	const [isPinned, setIsPinned] = useState(false);

	const [polygonsData, setPolygonsData] = useState<IChecklistEntity[]>([]);
	const [deletePolygons, setDeletePolygons] = useState<Array<Omit<IChecklistEntity, 'isChecked'>>>([]);
	const [editPolygon, setEditPolygon] = useState<Omit<IChecklistEntity, 'isChecked'> | null>(null);

	const containerRef = useRef<HTMLDivElement>(null);

	const handleViewPolygon = (data: Omit<IChecklistEntity, 'isChecked'>) => dispatch(setSelectedCustomPolygonForView(data.value));

	const handleChange = (data: IChecklistEntity[], lastChangedItem: ILastChangedItem) => {
		setPolygonsData(data);

		const selectedPolygonIds = data.reduce<Array<string>>((acc, curr) => {
			if (curr.isChecked) acc.push(curr.value);

			return acc;
		}, []);

		if (lastChangedItem.checked) dispatch((setSelectedCustomPolygonForView(lastChangedItem.id)));

		dispatch(setShowingCustomPolygonIds(selectedPolygonIds));
	};

	const handleReset = () => {
		setPolygonsData(prevData => prevData.map(item => ({ ...item, isChecked: false })));

		dispatch(resetActiveCustomPolygons());
		dispatch(resetShowingCustomPolygonIds());
	};

	const onClickDelete = (data: Omit<IChecklistEntity, 'isChecked'>) => {
		setDeletePolygons([data]);
		setIsDeleteConfirmationOpen(true);
	};

	const onClickEdit = (data: Omit<IChecklistEntity, 'isChecked'>) => {
		setEditPolygon(data);
		setIsEditPolygonOpen(true);
	};

	const closeEditPolygon = () => {
		setEditPolygon(null);
		setIsEditPolygonOpen(false);
	};

	const onClickBulkDelete = (data: Array<Omit<IChecklistEntity, 'isChecked'>>) => {
		setDeletePolygons(data);
		setIsDeleteConfirmationOpen(true);
	};

	const handleDeletePolygons = () => {
		const deletePolygonsIds = deletePolygons.map(polygon => polygon.value);

		deleteCustomPolygons(deletePolygonsIds);
		dispatch(hideCustomPolygonsOnMap(deletePolygonsIds));

		setIsDeleteConfirmationOpen(false);
		setDeletePolygons([]);
	};

	const toggleDeleteConfirmation = () => setIsDeleteConfirmationOpen(prevState => !prevState);

	const pinFilter = useCallback((filter: TEControlsType | null) => {
		return dispatch(setPinnedFilter(filter));
	}, [dispatch]);

	useEffect(() => {
		if (customPolygons) {
			const data = customPolygons.reduce<IChecklistEntity[]>((acc, curr) => {
				acc.push({
					value: curr.id,
					label: curr.name,
					isChecked: !!showingCustomPolygonIds.includes(curr.id),
				});

				return acc;
			}, []);

			setPolygonsData(data);
		}
	}, [customPolygons, showingCustomPolygonIds]);

	useEffect(() => {
		pinFilter(isPinned ? EControlsType.customPolygonList : null);
	}, [isPinned, pinFilter]);

	useOnClickOutside({
		refs: [containerRef],
		handler: () => dispatch(resetRightSideControls()),
		disableClickOutside: isPinned,
		fireEvent: 'click',
	});

	return (
		<div
			ref={containerRef}
			className={cn(controlStyles.controlItemContainer, styles.container, 'checklistContainer')}
		>
			<div className={styles.header}>
				Show polygons on map
				<PinAction
					isPinned={isPinned}
					pinTooltip='Pin'
					setIsPinned={setIsPinned}
					tooltipPlacement='left'
					unpinTooltip='Unpin'
				/>
			</div>
			<Checklist
				bulkDeleteActionTooltip='Delete selected polygons'
				bulkDeleteHandler={onClickBulkDelete}
				classNames={{
					submissionContainer: styles.checklistSubmissionButtonsContainer,
				}}
				clearLabel='Reset all'
				data={polygonsData}
				deleteActionTooltip='Delete polygon'
				deleteHandler={onClickDelete}
				editActionTooltip='Edit polygon'
				editHandler={onClickEdit}
				handleClear={handleReset}
				isSortedList
				isViewActionOnCheckedItems
				onChange={handleChange}
				searchDelay={SEARCH_DELAY_ON_MAP}
				searchPlaceholder='Search by polygon name'
				selectAllName='select-all-sites-polygons'
				viewActionTooltip='Allocate on the map'
				viewHandler={handleViewPolygon}
				viewIcon={Icons.viewLink}
			/>
			<Modal
				isOpen={isDeleteConfirmationOpen}
				onClose={toggleDeleteConfirmation}
				width='600px'
			>
				<Confirm
					onClose={toggleDeleteConfirmation}
					onSubmit={handleDeletePolygons}
					question={
						deletePolygons.length === 1
							? `Are you sure you want to delete the polygon: ${deletePolygons[0].label}?`
							: `Are you sure you want to delete selected ${deletePolygons.length} polygons?`
					}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyPolygonForm}
				isOpen={isEditPolygonOpen}
				onClose={closeEditPolygon}
				title='Edit Polygon'
				width={MODAL_WIDTH_480}
			>
				<CustomPolygonForm
					closeCustomPolygon={closeEditPolygon}
					customPolygons={customPolygons as unknown as ICustomPolygonDTO[]}
					isEdit={isEditPolygonOpen}
					polygonData={customPolygons.find((p) => p.id === editPolygon?.value) as TCustomPolygon}
					setCustomPolygonData={setEditPolygon as TSetCustomPolygonData}
					setIsDirtyForm={setIsDirtyPolygonForm}
					setIsEditingPolygon={setIsEditPolygonOpen}
				/>
			</Modal>
		</div>
	);
};

export default memo(CustomPolygonList);
